import React, { useState } from 'react';
import { signIn, completeNewPassword, sendMfaCode } from './auth';
import './Login.css';

function Login({ onLogin }) {
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [needsMfa, setNeedsMfa] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [showPolicy, setShowPolicy] = useState(null); // 'terms' | 'privacy' | 'refund'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const tokens = await signIn(email, password);
      onLogin(tokens.idToken);
    } catch (err) {
      if (err.code === 'NewPasswordRequired') {
        setCognitoUser(err.user);
        setNeedsNewPassword(true);
      } else if (err.code === 'MfaRequired' || err.code === 'TotpRequired') {
        setCognitoUser(err.user);
        setNeedsMfa(true);
      } else {
        setError(err.message || 'Authentication failed');
      }
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const tokens = await sendMfaCode(cognitoUser, mfaCode);
      onLogin(tokens.idToken);
    } catch (err) {
      setError(err.message || 'Invalid MFA code');
    }
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const tokens = await completeNewPassword(cognitoUser, newPassword);
      onLogin(tokens.idToken);
    } catch (err) {
      setError(err.message || 'Failed to set new password');
    }
  };

  if (showPolicy) {
    return (
      <div className="policy-page">
        <button className="policy-back" onClick={() => setShowPolicy(null)}>← Back</button>
        {showPolicy === 'terms' && (
          <div className="policy-content">
            <h1>Terms of Service</h1>
            <p><strong>Last updated:</strong> {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <h2>1. Service Description</h2>
            <p>Aiglon Flying Group provides aircraft flight hour tracking and cost management software for aviation clubs and individual pilots. The service enables users to log tacho entries, track fuel and oil costs, and manage payments for aircraft usage.</p>
            <h2>2. Subscription & Payments</h2>
            <p>Access to Aiglon Flying Group is provided on a subscription basis. Payments are processed securely via Stripe. By subscribing, you authorise us to charge your payment method on a recurring basis as per your selected plan.</p>
            <h2>3. Acceptable Use</h2>
            <p>You agree to use the service only for lawful purposes and in accordance with these terms. You are responsible for maintaining the confidentiality of your account credentials.</p>
            <h2>4. Data Accuracy</h2>
            <p>You are responsible for the accuracy of all data entered into the system, including tacho readings, fuel costs, and payment records.</p>
            <h2>5. Limitation of Liability</h2>
            <p>Aiglon Tracker is provided "as is". We are not liable for any inaccuracies in flight cost calculations or decisions made based on data within the platform.</p>
            <h2>6. Governing Law</h2>
            <p>These terms are governed by the laws of England and Wales.</p>
            <h2>7. Contact</h2>
            <p>For questions regarding these terms, contact us at <a href="mailto:mattlawrance79@gmail.com">mattlawrance79@gmail.com</a>.</p>
          </div>
        )}
        {showPolicy === 'privacy' && (
          <div className="policy-content">
            <h1>Privacy Policy</h1>
            <p><strong>Last updated:</strong> {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <h2>1. Data We Collect</h2>
            <p>We collect your name, email address, and flight/payment data you enter into the platform. Payment card details are handled exclusively by Stripe and are never stored on our servers.</p>
            <h2>2. How We Use Your Data</h2>
            <p>Your data is used solely to provide the Aiglon Tracker service — calculating balances, displaying logs, and processing payments. We do not sell or share your data with third parties except as required to deliver the service (e.g. Stripe for payment processing, AWS for hosting).</p>
            <h2>3. Data Storage</h2>
            <p>Data is stored securely on AWS infrastructure in the EU (eu-west-2 region). Access is restricted to authenticated users only.</p>
            <h2>4. Your Rights</h2>
            <p>Under UK GDPR, you have the right to access, correct, or delete your personal data. To exercise these rights, contact us at <a href="mailto:support@aiglontracker.com">support@aiglontracker.com</a>.</p>
            <h2>5. Cookies</h2>
            <p>We use session cookies solely for authentication purposes. No third-party tracking cookies are used.</p>
            <h2>6. Contact</h2>
            <p>Data controller: Matt Lawrance T/A Aiglon Flying Group. Email: <a href="mailto:mattlawrance79@gmail.com">mattlawrance79@gmail.com</a></p>
          </div>
        )}
        {showPolicy === 'refund' && (
          <div className="policy-content">
            <h1>Refund & Cancellation Policy</h1>
            <p><strong>Last updated:</strong> {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <h2>Cancellation</h2>
            <p>You may cancel your subscription at any time from your account settings. Cancellation takes effect at the end of the current billing period. You will retain access to the service until that date.</p>
            <h2>Refunds</h2>
            <p>Subscription fees are non-refundable except where required by law. If you believe you have been charged in error, please contact us within 14 days of the charge at <a href="mailto:support@aiglontracker.com">support@aiglontracker.com</a> and we will review your case.</p>
            <h2>Aircraft Usage Payments</h2>
            <p>Payments made for aircraft usage (tacho-based charges) are processed between club members and are subject to the club's own policies. Aiglon Tracker facilitates these payments but is not responsible for disputes between members.</p>
            <h2>Contact</h2>
            <p>For refund requests or billing queries: <a href="mailto:mattlawrance79@gmail.com">mattlawrance79@gmail.com</a></p>
          </div>
        )}
      </div>
    );
  }

  const loginForm = () => {
    if (needsMfa) {
      return (
        <form onSubmit={handleMfaSubmit}>
          <h3>Enter MFA Code</h3>
          <input type="text" placeholder="MFA Code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required />
          {error && <div className="error">{error}</div>}
          <button type="submit">Verify</button>
        </form>
      );
    }
    if (needsNewPassword) {
      return (
        <form onSubmit={handleNewPasswordSubmit}>
          <h3>Set New Password</h3>
          <input type="password" placeholder="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
          {error && <div className="error">{error}</div>}
          <button type="submit">Set Password</button>
        </form>
      );
    }
    return (
      <form onSubmit={handleSubmit}>
        <h3>Member Sign In</h3>
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {error && <div className="error">{error}</div>}
        <button type="submit">Sign In</button>
      </form>
    );
  };

  return (
    <div className="landing-page">
      {/* Hero */}
      <header className="landing-header">
        <div className="landing-logo">✈ Aiglon Flying Group</div>
        <nav className="landing-nav">
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#contact">Contact</a>
        </nav>
      </header>

      <section className="hero-section">
        <div className="hero-text">
          <h1>Aircraft Hour Tracking for Aiglon Flying Group</h1>
          <p>Aiglon Tracker helps flying clubs and shared aircraft owners accurately log tacho hours, track fuel and oil costs, and manage member payments — all in one place.</p>
        </div>
        <div className="login-card">
          {loginForm()}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="features-section">
        <h2>What We Offer</h2>
        <div className="features-grid">
          <div className="feature-item">
            <span>🕐</span>
            <h3>Tacho Hour Logging</h3>
            <p>Log start and finish tacho readings for every flight. Hours are calculated automatically.</p>
          </div>
          <div className="feature-item">
            <span>⛽</span>
            <h3>Fuel & Oil Tracking</h3>
            <p>Record fuel litres and oil quarts with costs. Receipt uploads required for accountability.</p>
          </div>
          <div className="feature-item">
            <span>💷</span>
            <h3>Balance Management</h3>
            <p>Real-time balance calculation: (Tacho Hours × Hourly Rate) minus fuel and oil costs.</p>
          </div>
          <div className="feature-item">
            <span>📅</span>
            <h3>Aircraft Scheduling</h3>
            <p>View and manage aircraft availability with the built-in scheduling calendar.</p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="pricing-section">
        <h2>Pricing</h2>
        <p className="pricing-intro">Simple, transparent pricing for flying clubs of all sizes.</p>
        <div className="pricing-grid">
          <div className="pricing-card">
            <h3>Club Plan</h3>
            <div className="price">£130<span>/month</span></div>
            <ul>
              <li>Up to 10 aircraft</li>
              <li>Unlimited members</li>
              <li>Full tacho & cost tracking</li>
              <li>Manager dashboard</li>
              <li>CSV data import</li>
              <li>Email support</li>
            </ul>
            <p className="pricing-note">All payments are processed securely via Stripe. Prices shown exclude VAT where applicable.</p>
          </div>
        </div>
        <p className="pricing-contact">Need a custom plan? <a href="mailto:mattlawrance79@gmail.com">Contact us</a>.</p>
      </section>

      {/* Contact */}
      <section id="contact" className="contact-section">
        <h2>Contact Us</h2>
        <div className="contact-details">
          <div className="contact-item">
            <strong>Email</strong>
            <a href="mailto:mattlawrance79@gmail.com">mattlawrance79@gmail.com</a>
          </div>
          <div className="contact-item">
            <strong>Business</strong>
            <span>Matt Lawrance T/A Aiglon Flying Group</span>
          </div>
          <div className="contact-item">
            <strong>Location</strong>
            <span>United Kingdom</span>
          </div>
          <div className="contact-item">
            <strong>Support Hours</strong>
            <span>Mon–Fri, 9am–5pm GMT</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <p>© {new Date().getFullYear()} Matt Lawrance T/A Aiglon Flying Group. All rights reserved.</p>
        <div className="footer-links">
          <button onClick={() => setShowPolicy('terms')}>Terms of Service</button>
          <button onClick={() => setShowPolicy('privacy')}>Privacy Policy</button>
          <button onClick={() => setShowPolicy('refund')}>Refund Policy</button>
        </div>
      </footer>
    </div>
  );
}

export default Login;
